<template>
  <div class="floating-bug-report">
    <v-tooltip left>
      <template v-slot:activator="{ on, attrs }">
        <v-btn
          id="floating-bug-report-trigger"
          fab
          fixed
          bottom
          left
          color="#1ba8bb"
          class="white--text floating-bug-fab"
          v-bind="attrs"
          v-on="on"
          @click="openBugDialog"
        >
          <v-icon>mdi-bug-outline</v-icon>
        </v-btn>
      </template>
      <span>Report a bug</span>
    </v-tooltip>

    <v-dialog v-model="bugDialog" persistent max-width="550">
      <v-card class="pa-6 radius-21">
        <div class="d-flex align-center justify-space-between mb-2">
          <h4 class="cyan-text font-weight-500 mb-0">Report a bug</h4>
          <v-icon
            @click="closeBugDialog"
            color="#1ba8bb"
            style="font-size: 30px"
            >mdi-close-circle</v-icon
          >
        </div>
        <p class="mb-4" style="color: #5f5f5f">
          Capture what you see on the dashboard and tell us what went wrong.
        </p>
        <v-alert v-if="bugSubmitError" type="error" dense text class="mb-4">
          {{ bugSubmitError }}
        </v-alert>
        <v-textarea
          solo
          flat
          auto-grow
          label="Describe the issue"
          background-color="#E1F2FF"
          :error-messages="bugDescriptionError ? [bugDescriptionError] : []"
          v-model="bugDescription"
          @input="bugDescriptionError = ''"
        ></v-textarea>

        <div class="mt-5">
          <div class="d-flex align-center justify-space-between mb-2">
            <div class="font-weight-500">Screenshot</div>
            <div class="d-flex align-center">
              <v-btn
                small
                text
                color="var(--primary-color)"
                class="px-2 ml-1"
                @click="
                  $refs.bugFileInput &&
                    $refs.bugFileInput.$refs.input &&
                    $refs.bugFileInput.$refs.input.click()
                "
              >
                Upload
              </v-btn>
            </div>
          </div>

          <v-card outlined class="pa-3 bug-screenshot-card">
            <div v-if="bugCaptureError" class="error--text mb-2">
              {{ bugCaptureError }}
            </div>
            <div v-if="bugScreenshotPreview" class="text-center">
              <v-img
                :src="bugScreenshotPreview"
                max-height="260"
                contain
                class="rounded"
              ></v-img>
            </div>
            <div v-else class="text-center grey--text">
              No screenshot yet. Capture or upload one to continue.
            </div>

            <v-file-input
              ref="bugFileInput"
              prepend-icon=""
              dense
              hide-details
              flat
              solo
              class="mt-3"
              accept="image/*"
              label="Or upload a screenshot"
              background-color="#E1F2FF"
              @change="onScreenshotFileChange"
            ></v-file-input>
          </v-card>
        </div>

        <v-card-actions class="justify-end px-0 mt-6">
          <v-btn text class="mr-2" @click="closeBugDialog">Cancel</v-btn>
          <v-btn
            color="var(--primary-color)"
            class="white--text"
            :loading="bugLoading"
            @click="submitBugReport"
          >
            Send
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import html2canvas from "html2canvas";

export default {
  name: "FloatingBugReport",
  data() {
    return {
      bugDialog: false,
      bugDescription: "",
      bugDescriptionError: "",
      bugScreenshotPreview: "",
      bugScreenshotFile: null,
      bugLoading: false,
      bugCaptureError: "",
      bugSubmitError: "",
      captureInProgress: false,
    };
  },
  computed: {
    ...mapGetters(["getUserId"]),
  },
  methods: {
    openBugDialog() {
      this.resetBugForm();
      setTimeout(() => {
        this.bugDialog = true;
      }, 1200);
      this.$nextTick(() => {
        setTimeout(() => this.captureScreenshot(), 200);
      });
    },
    closeBugDialog() {
      this.bugDialog = false;
      this.resetBugForm();
    },
    resetBugForm() {
      this.bugDescription = "";
      this.bugDescriptionError = "";
      this.bugCaptureError = "";
      this.bugSubmitError = "";
      this.bugScreenshotPreview = "";
      this.bugScreenshotFile = null;
      this.captureInProgress = false;
      this.bugLoading = false;
    },
    dataURLtoBlob(dataUrl) {
      const arr = dataUrl.split(",");
      const mime = (arr[0].match(/:(.*?);/) || [])[1] || "image/png";
      const bstr = atob(arr[1]);
      let n = bstr.length;
      const u8arr = new Uint8Array(n);
      while (n--) u8arr[n] = bstr.charCodeAt(n);
      return new Blob([u8arr], { type: mime });
    },
    async captureScreenshot() {
      const bodyW = document.body
        ? Math.max(document.body.scrollWidth, document.body.offsetWidth || 0)
        : 0;
      const docW = document.documentElement
        ? Math.max(
            document.documentElement.scrollWidth,
            document.documentElement.offsetWidth || 0
          )
        : 0;
      const target =
        document.body && (bodyW >= docW || !document.documentElement)
          ? document.body
          : document.documentElement || document.body;
      if (!target) {
        this.bugCaptureError =
          "Unable to find the page to capture. Please upload a screenshot instead.";
        return;
      }

      const originalX = window.scrollX;
      const originalY = window.scrollY;
      const fullWidth = Math.max(
        document.body.scrollWidth,
        document.documentElement.scrollWidth,
        document.body.offsetWidth || 0,
        document.documentElement.offsetWidth || 0,
        document.body.clientWidth || 0,
        document.documentElement.clientWidth || 0,
        window.innerWidth || 0
      );
      const fullHeight = Math.max(
        document.body.scrollHeight,
        document.documentElement.scrollHeight,
        document.body.offsetHeight || 0,
        document.documentElement.offsetHeight || 0,
        document.body.clientHeight || 0,
        document.documentElement.clientHeight || 0,
        window.innerHeight || 0
      );
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const maxCanvas = 8192;
      const scale = Math.max(
        1,
        Math.min(
          dpr,
          fullWidth > 0 ? maxCanvas / fullWidth : dpr,
          fullHeight > 0 ? maxCanvas / fullHeight : dpr
        )
      );

      this.captureInProgress = true;
      this.bugCaptureError = "";
      try {
        window.scrollTo(0, 0);
        const canvas = await html2canvas(target, {
          useCORS: true,
          logging: false,
          backgroundColor: "#ffffff",
          scale,
          width: fullWidth,
          height: fullHeight,
          windowWidth: fullWidth,
          windowHeight: fullHeight,
          scrollX: 0,
          scrollY: 0,
          imageTimeout: 0,
        });
        let dataUrl;
        try {
          dataUrl = canvas.toDataURL("image/png");
        } catch (dataUrlError) {
          console.error("toDataURL failed", dataUrlError);
          this.bugCaptureError =
            "Screenshot restricted by browser. Please upload an image instead.";
          return;
        }
        this.bugScreenshotPreview = dataUrl;
        let blob = null;
        try {
          blob = await new Promise((resolve) =>
            canvas.toBlob((blobResult) => resolve(blobResult), "image/png")
          );
        } catch (toBlobError) {
          console.warn("toBlob failed, using dataURL fallback", toBlobError);
        }
        if (!blob && dataUrl) blob = this.dataURLtoBlob(dataUrl);
        if (blob) {
          this.bugScreenshotFile = new File(
            [blob],
            `bug-report-${Date.now()}.png`,
            { type: "image/png" }
          );
        } else {
          this.bugCaptureError =
            "Could not create the screenshot file. Try uploading an image instead.";
        }
      } catch (error) {
        console.error("Screenshot capture failed", error);
        this.bugCaptureError =
          "Screenshot failed. Try again or upload an image instead.";
      } finally {
        window.scrollTo(originalX, originalY);
        this.captureInProgress = false;
      }
    },
    onScreenshotFileChange(file) {
      this.bugCaptureError = "";
      this.bugSubmitError = "";
      if (!file) {
        this.bugScreenshotPreview = "";
        this.bugScreenshotFile = null;
        return;
      }
      this.bugScreenshotFile = file;
      const reader = new FileReader();
      reader.onload = (e) => {
        this.bugScreenshotPreview = e.target.result;
      };
      reader.readAsDataURL(file);
    },
    async submitBugReport() {
      this.bugDescriptionError = "";
      this.bugSubmitError = "";
      if (!this.bugDescription || !this.bugDescription.trim()) {
        this.bugDescriptionError = "Please describe the issue.";
        return;
      }
      if (!this.bugScreenshotFile) {
        this.bugCaptureError =
          "Add a screenshot by capturing or uploading before sending.";
        return;
      }
      const formData = new FormData();
      formData.append("description", this.bugDescription.trim());
      if (this.getUserId) formData.append("userId", this.getUserId);
      formData.append("imageFile", this.bugScreenshotFile);
      this.bugLoading = true;
      try {
        const response = await this.$store.dispatch("reportBug", formData);
        const succeeded =
          response?.data?.succeeded === true || response?.status === 200;
        if (succeeded) {
          this.$swal.fire({
            position: "top-end",
            icon: "success",
            title:
              "A member from our team will contact you within the next 24hrs once the bug is resolved.",
            showConfirmButton: false,
            timer: 3000,
            toast: true,
          });
          this.closeBugDialog();
        } else {
          this.bugSubmitError =
            response?.data?.message ||
            "Unable to send the bug report. Please try again.";
        }
      } catch (error) {
        console.error("Bug report failed", error);
        this.bugSubmitError =
          "Unable to send the bug report. Please try again.";
      } finally {
        this.bugLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.floating-bug-fab {
  z-index: 999;
  margin-bottom: 24px;
  margin-right: 24px;
}
.bug-screenshot-card {
  max-height: 320px;
  overflow: auto;
}
</style>
